import type { ApolloError } from "@apollo/client";
import PubSub from "pubsub-js";
import type { StateCreator } from "zustand";

import { PubSubActions } from "@/global/pubsub-actions.ts";
import { notify } from "@/modules/notifications/notify";
import { getActivitiesQuery } from "@/modules/workflow/graphql/queries.ts";
import { workflowIntl } from "@/modules/workflow/i18n/intl.ts";
import type {
  WorkflowActivitySlice,
  WorkflowActivitySliceState,
  WorkflowEditorStore,
} from "@/modules/workflow/stores/workflow-editor-store-types";
import {
  extractGetActivitiesData,
  findLatestVersion,
  getValueFromWorkflowParameter,
} from "@/modules/workflow/utils/activity.ts";
import type { Activity, WorkflowParameter } from "@/modules/workflow/utils/types.ts";

export const initialActivitySliceState: WorkflowActivitySliceState = {
  isLoadingActivities: false,
  activities: [],
  activityErrors: [],
};

export const createActivitySlice: StateCreator<WorkflowEditorStore, [], [], WorkflowActivitySlice> = (set, get) => {
  PubSub.subscribe(PubSubActions.LOGOUT, () => {
    set(initialActivitySliceState);
  });

  return {
    ...initialActivitySliceState,
    loadActivities: async () => {
      set({ isLoadingActivities: true });
      // We need to preload activities to fill and validate activities parameters
      const { data: activitiesData, error } = await getActivitiesQuery({});

      if (!error && activitiesData) {
        const activitiesResult = extractGetActivitiesData(activitiesData);
        const activities = activitiesResult.activities;

        if (activities) {
          // We generate all available versions by activity
          const clonedActivities: Activity[] = [...activities];

          for (const activity of clonedActivities) {
            const sameActivities = activities.filter((act) => act.name === activity.name);
            const withDefaultTrue = sameActivities.filter((act) => act.default);
            // Further investigation is needed to understand which one is really the default one
            if (withDefaultTrue.length != 1) {
              const allVersions = sameActivities.map((act) => act.version.name);
              const latestVersion = findLatestVersion(allVersions);
              // If we found the latest version, we set all the others to false. This actually suggests that by following
              // semantic versioning, the latest version is the default one.
              if (latestVersion) {
                for (const act of sameActivities) {
                  act.default = act.version.name === latestVersion;
                }
              }
            }
            activity.versions = sameActivities.map((act) => ({ ...act.version, default: act.default }));
          }

          set({ isLoadingActivities: false, activities: clonedActivities });
        }

        if (activitiesResult.errors) {
          set({ activityErrors: activitiesResult.errors });
        }
      } else {
        set({ isLoadingActivities: false, isLoadingWorkflow: false });
        const __error = new Error(
          (error as ApolloError | undefined)?.message ?? workflowIntl.formatMessage({ id: "an error occurred" }),
        );

        notify.fromErrors({
          title: "Loading Workflow Activities Error",
          errors: [__error],
        });

        throw __error;
      }
    },
    async switchActivityVersion(nodeId, version) {
      const selectedActivity = get().selectedActivity;
      const instance = get().instance;
      const node = get().getNode(nodeId);

      if (node) {
        // Update/Create the Node with the new version
        const cloneNode = { ...node };
        cloneNode.data.version = version;

        // Fully delete the node so that its edges and parameters are removed
        // since the new version might have different inputs/outputs
        instance?.deleteElements({ nodes: [node] }).then(() => instance?.addNodes(cloneNode));

        // We had a new Node, but it has the same Node ID,
        // so we just update the selectedActivity with the new version
        if (selectedActivity?.nodeId === nodeId) {
          selectedActivity.version = version;
          set({ selectedActivity });
        }

        get().resetActivityNodeErrors();

        // Set new parameters (default, mandatory)
        await get().registerActivityParameters(nodeId);
        get().checkAllParameters();
      }
    },
    async restoreActivityDefaultParams(nodeId: string, parameterIds?: WorkflowParameter["parameterId"][]) {
      const node = get().getNode(nodeId);
      if (!node) {
        return;
      }

      const activity = get().getActivity(node.data.activityName, node.data.version);

      if (activity) {
        // if no parameters are given reset all of them
        const parametersToReset =
          parameterIds && Array.isArray(parameterIds)
            ? (activity.parameters?.filter((p) => parameterIds?.includes(p.id)) ?? [])
            : (activity.parameters ?? []);

        for (const parameter of parametersToReset) {
          const defaultValues = await getValueFromWorkflowParameter(parameter, undefined, true);
          get().setActivityParameterValue(nodeId, parameter.id, defaultValues);
        }
      }
    },
    getActivity: (activityName, version) => {
      return (
        get().activities.find((activity) => activity.name === activityName && activity.version.name === version) ?? null
      );
    },
  };
};
